import sha512 from 'js-sha512';
import aesjs from "aes-js";
import {
    API_URL,
    PROFORMAS_API_URL,
    RSA_PUBLIC_KEY
} from "./platformsHandlerServices.json"

/**
 * Efika Platforms Handler
 * Maneja la comunicación con los endpoints públicos del API de Kakao Platforms
 * Implementa cifrado híbrido RSA+AES para seguridad
 */
class EfikaPlatformsHandler {
    constructor(hashAlgorithm = 'SHA-1') {
        this.apiUrl = API_URL;
        this.publicKey = RSA_PUBLIC_KEY;
        this.rsaPublicKeyObject = null;
        this.hashAlgorithm = hashAlgorithm; // 'SHA-1' por defecto para compatibilidad con Node.js
    }

    /**
     * Convierte una clave pública PEM a formato CryptoKey
     * @param {string} pemKey - Clave pública en formato PEM
     * @param {string} hashAlgorithm - Algoritmo hash ('SHA-1' o 'SHA-256')
     * @returns {Promise<CryptoKey>} Clave pública como CryptoKey
     */
    async pemToKey(pemKey, hashAlgorithm = 'SHA-1') {
        // Remover headers y footers del PEM
        const pemHeader = "-----BEGIN PUBLIC KEY-----";
        const pemFooter = "-----END PUBLIC KEY-----";
        const pemContents = pemKey.replace(pemHeader, '').replace(pemFooter, '').replace(/\s/g, '');

        // Convertir de base64 a ArrayBuffer
        const binaryString = atob(pemContents);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }

        // Importar la clave con el hash especificado
        return await window.crypto.subtle.importKey(
            'spki',
            bytes.buffer,
            {
                name: 'RSA-OAEP',
                hash: hashAlgorithm
            },
            false,
            ['encrypt']
        );
    }

    /**
     * Inicializa la clave RSA
     */
    async initializeRSAKey() {
        if (!this.rsaPublicKeyObject) {
            this.rsaPublicKeyObject = await this.pemToKey(this.publicKey, this.hashAlgorithm);
        }
    }

    /**
     * Hashea una contraseña usando SHA-512
     * @param {string} password - Contraseña en texto plano
     * @returns {string} Hash SHA-512 de la contraseña
     */
    hashPassword(password) {
        return sha512.sha512(password);
    }

    /**
     * Convierte string a Uint8Array
     * @param {string} str - String a convertir
     * @returns {Uint8Array} Array de bytes
     */
    stringToBytes(str) {
        return aesjs.utils.utf8.toBytes(str);
    }

    /**
     * Convierte Uint8Array a base64
     * @param {Uint8Array} bytes - Array de bytes
     * @returns {string} String en base64
     */
    bytesToBase64(bytes) {
        return btoa(String.fromCharCode(...bytes));
    }

    /**
     * Genera bytes aleatorios
     * @param {number} length - Número de bytes
     * @returns {Uint8Array} Array de bytes aleatorios
     */
    randomBytes(length) {
        return window.crypto.getRandomValues(new Uint8Array(length));
    }

    /**
     * Cifra datos usando RSA-OAEP
     * @param {Uint8Array} data - Datos a cifrar
     * @returns {Promise<string>} Datos cifrados en base64
     */
    async encryptRSAOAEP(data) {
        await this.initializeRSAKey();

        try {
            const encrypted = await window.crypto.subtle.encrypt(
                {
                    name: 'RSA-OAEP'
                },
                this.rsaPublicKeyObject,
                data
            );

            return this.bytesToBase64(new Uint8Array(encrypted));
        } catch (error) {
            console.error('Error en RSA-OAEP encryption:', error);
            console.error('Hash algorithm usado:', this.hashAlgorithm);
            console.error('Tamaño de datos:', data.length);
            throw error;
        }
    }

    /**
     * Método de prueba para verificar compatibilidad
     * @returns {Promise<object>} Información de configuración
     */
    async testConfiguration() {
        await this.initializeRSAKey();

        return {
            hashAlgorithm: this.hashAlgorithm,
            keyAlgorithm: this.rsaPublicKeyObject.algorithm,
            keyUsages: this.rsaPublicKeyObject.usages,
            keyType: this.rsaPublicKeyObject.type
        };
    }

    /**
     * Cifra datos usando cifrado híbrido RSA+AES
     * @param {object} data - Datos a cifrar
     * @returns {Promise<object>} Objeto con datos cifrados
     */
    async encryptHybrid(data) {
        try {
            // 1. Generar clave AES aleatoria de 256 bits (32 bytes) y IV de 128 bits (16 bytes)
            const aesKey = this.randomBytes(32);
            const iv = this.randomBytes(16);

            // 2. Convertir datos a bytes
            const dataBytes = this.stringToBytes(JSON.stringify(data));

            // 3. Cifrar datos con AES-256-CBC
            const aesCbc = new aesjs.ModeOfOperation.cbc(aesKey, iv);
            const paddedData = aesjs.padding.pkcs7.pad(dataBytes);
            const encryptedBytes = aesCbc.encrypt(paddedData);

            // 4. Cifrar clave AES con RSA-OAEP
            const encryptedKey = await this.encryptRSAOAEP(aesKey);

            return {
                encryptedKey: encryptedKey,
                encryptedData: this.bytesToBase64(encryptedBytes),
                iv: this.bytesToBase64(iv)
            };
        } catch (error) {
            console.error('Error al cifrar datos:', error.message);
            throw new Error('Error en el proceso de cifrado');
        }
    }

    /**
     * Realiza una petición HTTP al API
     * @param {string} endpoint - Endpoint del API
     * @param {object} data - Datos a enviar
     * @returns {Promise<object>} Respuesta del servidor
     */
    async makeRequest(endpoint, data, apiUrl = this.apiUrl) {
        try {
            const response = await fetch(`${apiUrl}${endpoint}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(data)
            });

            if (!response.ok) {
                throw new Error(`HTTP Error: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error(`Error en petición a ${endpoint}:`, error.message);
            throw error;
        }
    }

    /**
     * Autentica un usuario en el sistema
     * @param {string} loginIdentifier - Email o username del usuario
     * @param {string} password - Contraseña en texto plano
     * @returns {Promise<object>} Datos del usuario autenticado
     */
    async login(loginIdentifier, password) {
        try {
            // Preparar datos para cifrar
            const loginData = {
                loginIdentifier: loginIdentifier,
                password: this.hashPassword(password)
            };

            // Cifrar datos
            const encryptedRequest = await this.encryptHybrid(loginData);

            // Realizar petición
            const response = await this.makeRequest('/login', encryptedRequest);

            return response

        } catch (error) {
            console.error('Error en login:', error.message);
            return {
                status: 500,
            };
        }
    }

    /**
     * Cambia la contraseña de un usuario (requiere contraseña actual)
     * @param {string} identifier - Email o username del usuario
     * @param {string} currentPassword - Contraseña actual en texto plano
     * @param {string} newPassword - Nueva contraseña en texto plano
     * @returns {Promise<object>} Resultado de la operación
     */
    async changePassword(identifier, currentPassword, newPassword) {
        try {
            // Preparar datos para cifrar
            const changePasswordData = {
                identifier: identifier,
                currentPassword: this.hashPassword(currentPassword),
                newPassword: this.hashPassword(newPassword)
            };

            // Cifrar datos
            const encryptedRequest = await this.encryptHybrid(changePasswordData);

            // Realizar petición
            const response = await this.makeRequest('/change-password', encryptedRequest);
            return response

        } catch (error) {
            console.error('Error en changePassword:', error.message);
            return {
                status: 500,
            };
        }
    }

    /**
     * Solicita recuperación de contraseña por email
     * @param {string} email - Email del usuario
     * @returns {Promise<object>} Resultado de la operación
     */
    async forgotPassword(email) {
        try {
            // Validar email
            if (!email || !this.isValidEmail(email)) {
                return {
                    success: 500,
                    error: 'INVALID_EMAIL'
                };
            }

            // Preparar datos para cifrar
            const forgotPasswordData = {
                email: email
            };

            // Cifrar datos
            const encryptedRequest = await this.encryptHybrid(forgotPasswordData);

            // Realizar petición
            const response = await this.makeRequest('/forgot-password', encryptedRequest);

            return response

        } catch (error) {
            console.error('Error en forgotPassword:', error.message);
            return {
                success: 500
            };
        }
    }

    /**
     * Solicita listado de proformas
     * @param {string} vendorId - Id vendedor de wordpress
     * @returns {Promise<object>} Resultado de la operación
     */
    async listProformas(vendorId) {
        try {
            // Preparar datos para cifrar
            const listProformasData = {
                vendorId: vendorId
            };

            // Cifrar datos
            const encryptedRequest = await this.encryptHybrid(listProformasData);

            // Realizar petición
            const response = await this.makeRequest('/list-proformas', encryptedRequest, PROFORMAS_API_URL);

            return response

        } catch (error) {
            console.error('Error en listProformas:', error.message);
            return {
                success: 500
            };
        }
    }

    
    /**
     * Creacion de proforma
     * @param {object} proformaData - objeto con la infomacion de la proforma
     * @returns {Promise<object>} Resultado de la operación
     */
    async postProformas(proformaData) {
        try {
            // Cifrar datos
            const encryptedRequest = await this.encryptHybrid(proformaData);

            // Realizar petición
            const response = await this.makeRequest('/proformas', encryptedRequest, PROFORMAS_API_URL);

            return response

        } catch (error) {
            console.error('Error en listProformas:', error.message);
            return {
                success: 500
            };
        }
    }

    /**
     * Valida formato de email
     * @param {string} email - Email a validar
     * @returns {boolean} True si el email es válido
     */
    isValidEmail(email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }
}

export default EfikaPlatformsHandler;